import type { Http } from '../http';
import { endpoints } from '../endpoints';
import type { RequestOptions } from '../types/common';
import type {
  RawAccountDetailResponse,
  RawAccountLoginIdCheckResponse,
  RawAccountPageResponse,
  RawAccountPasswordResetRequest,
  RawAccountPasswordResetResponse,
  RawAccountPlantResponse,
  RawAccountPlantRoleRequest,
  RawAccountPlantsAddRequest,
  RawAccountPlantsAddResponse,
  RawAccountPlantsResponse,
  RawAccountPostRequest,
  RawAccountPutRequest,
  RawAccountSearchParams,
} from '../types/account';

export function createAccountApi(http: Http) {
  return {
    /** GET /api/v2/accounts — 계정 목록 조회 */
    async search(
      params?: RawAccountSearchParams,
      options?: RequestOptions,
    ): Promise<RawAccountPageResponse> {
      const res = await http.get<RawAccountPageResponse>(endpoints.account.base, {
        params,
        signal: options?.signal,
      });
      return res.data;
    },

    /** GET /api/v2/accounts/{accountId} — 계정 단건 조회. accountId 는 BE String 직렬화라 string. [§5-7] */
    async getById(accountId: string, options?: RequestOptions): Promise<RawAccountDetailResponse> {
      const res = await http.get<RawAccountDetailResponse>(endpoints.account.byId(accountId), {
        signal: options?.signal,
      });
      return res.data;
    },

    /** POST /api/v2/accounts — 계정 등록(비밀번호는 BE 랜덤 생성). SYSTEM_ADMIN 전용. 409=loginId 중복 */
    async create(data: RawAccountPostRequest): Promise<RawAccountDetailResponse> {
      const res = await http.post<RawAccountDetailResponse>(endpoints.account.base, data);
      return res.data;
    },

    /** PUT /api/v2/accounts/{accountId} — 계정 정보 수정 */
    async update(accountId: number, data: RawAccountPutRequest): Promise<RawAccountDetailResponse> {
      const res = await http.put<RawAccountDetailResponse>(endpoints.account.byId(accountId), data);
      return res.data;
    },

    /**
     * DELETE /api/v2/accounts/{accountId} — 계정 삭제(soft delete). 400=구독 상태로 삭제 불가 / 403=권한 없음 / 404=계정 없음.
     */
    async remove(accountId: string | number): Promise<void> {
      await http.delete<void>(endpoints.account.byId(accountId));
    },

    /** GET /api/v2/accounts/check — 로그인 ID 사용 가능 여부 조회 */
    async checkLoginId(
      loginId: string,
      options?: RequestOptions,
    ): Promise<RawAccountLoginIdCheckResponse> {
      const res = await http.get<RawAccountLoginIdCheckResponse>(endpoints.account.check, {
        params: { loginId },
        signal: options?.signal,
      });
      return res.data;
    },

    /** POST /api/v2/accounts/{accountId}/password-reset — 계정 비밀번호 초기화 */
    async resetPassword(
      accountId: number,
      data?: RawAccountPasswordResetRequest,
    ): Promise<RawAccountPasswordResetResponse> {
      const res = await http.post<RawAccountPasswordResetResponse>(
        endpoints.account.passwordReset(accountId),
        data ?? null,
      );
      return res.data;
    },

    /** GET /api/v2/accounts/{accountId}/plants — 계정별 발전소 목록 조회 */
    async getPlants(
      accountId: number,
      options?: RequestOptions,
    ): Promise<RawAccountPlantsResponse> {
      const res = await http.get<RawAccountPlantsResponse>(endpoints.account.plants(accountId), {
        signal: options?.signal,
      });
      return res.data;
    },

    /** GET /api/v2/accounts/{accountId}/plants/{plantId} — 계정별 발전소 단건 조회 */
    async getPlant(
      accountId: number,
      plantId: number,
      options?: RequestOptions,
    ): Promise<RawAccountPlantResponse> {
      const res = await http.get<RawAccountPlantResponse>(
        endpoints.account.plantById(accountId, plantId),
        { signal: options?.signal },
      );
      return res.data;
    },

    /** PUT /api/v2/accounts/{accountId}/plants/{plantId} — 계정 발전소 단건 연동 추가 */
    async addPlant(accountId: number, plantId: number): Promise<void> {
      await http.put<void>(endpoints.account.plantById(accountId, plantId));
    },

    /** POST /api/v2/accounts/{accountId}/plants — 계정 발전소 복수 연동 추가 */
    async addPlants(
      accountId: number,
      data: RawAccountPlantsAddRequest,
    ): Promise<RawAccountPlantsAddResponse> {
      const res = await http.post<RawAccountPlantsAddResponse>(
        endpoints.account.plants(accountId),
        data,
      );
      return res.data;
    },

    /** DELETE /api/v2/accounts/{accountId}/plants/{plantId} — 계정 발전소 연동 삭제 */
    async deletePlant(accountId: number, plantId: number): Promise<void> {
      await http.delete<void>(endpoints.account.plantById(accountId, plantId));
    },

    /**
     * POST /api/v2/accounts/{accountId}/plant/{plantId}/roles — 계정-발전소 역할 설정.
     * OWNR/SAFE/INST + 대표 여부. SYSTEM_ADMIN 전용. 응답 shape 미확정 → void(소비측은 목록 재조회).
     */
    async setPlantRole(
      accountId: number,
      plantId: number,
      data: RawAccountPlantRoleRequest,
    ): Promise<void> {
      await http.post<void>(endpoints.account.plantRoles(accountId, plantId), data);
    },

    /**
     * DELETE /api/v2/accounts/{accountId}/plant-roles/{plantId} — 계정-발전소 역할 삭제(연동 해제).
     * 사용 중 구독·READY ticket 있으면 실패(BE 에러). SYSTEM_ADMIN 전용.
     */
    async deletePlantRole(accountId: number, plantId: number): Promise<void> {
      await http.delete<void>(endpoints.account.deletePlantRole(accountId, plantId));
    },
  };
}
